/**
 * ETA relative-time formatting. Timezone-safe: the relative part is computed from
 * two absolute instants (UTC ETA vs. now); the exact part is viewer-local.
 */

import { formatLocalDateTime } from '../../../lib/datetime';

export type EtaUrgency = 'normal' | 'soon' | 'overdue';

/** Future ETAs closer than this render in the amber "due soon" state. */
const SOON_MS = 24 * 60 * 60 * 1000;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/**
 * Largest-unit human span for a non-negative duration, e.g. "45 minutes", "3 days".
 * Returns null for < 1 minute so the caller can render "arriving now"/"overdue".
 */
function humanizeSpan(ms: number): string | null {
  const minutes = Math.round(ms / MINUTE_MS);
  if (minutes < 1) return null;
  if (minutes < 60) return plural(minutes, 'minute');
  const hours = Math.round(ms / HOUR_MS);
  if (hours < 24) return plural(hours, 'hour');
  const days = Math.round(ms / DAY_MS);
  return plural(days, 'day');
}

/** Exact stamp, viewer-local — the same shared formatter the Updated column uses. */
export function formatEtaExact(iso: string): string {
  return formatLocalDateTime(iso);
}

/** Neutral fallback for an absent or unparseable ETA. */
const EMPTY_ETA: EtaDescription = { relative: '—', exact: '—', urgency: 'normal', overdue: false };

export interface EtaDescription {
  /** Headline, e.g. "Arriving in 3 days", "Arriving now", "Overdue by 2 hours". */
  relative: string;
  /** Exact stamp, viewer-local, e.g. "14 Aug 2026, 18:00". */
  exact: string;
  urgency: EtaUrgency;
  /** True once the ETA is in the past (content/script hasn't arrived in time). */
  overdue: boolean;
}

/**
 * Describe an ISO-8601 ETA relative to `now`: far-future, due-soon (< 24h), the
 * sub-minute "now" boundary, or overdue. `now` is injectable for testing.
 */
export function describeEta(iso: string, now: Date = new Date()): EtaDescription {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return EMPTY_ETA;
  const exact = formatEtaExact(iso);
  const diffMs = target - now.getTime();

  // Past (or exactly now, treated as due): overdue.
  if (diffMs <= 0) {
    const span = humanizeSpan(-diffMs);
    return {
      relative: span ? `Overdue by ${span}` : 'Overdue',
      exact,
      urgency: 'overdue',
      overdue: true,
    };
  }

  const span = humanizeSpan(diffMs);
  return {
    relative: span ? `Arriving in ${span}` : 'Arriving now',
    exact,
    urgency: diffMs < SOON_MS ? 'soon' : 'normal',
    overdue: false,
  };
}
